import { useCallback, useMemo, useState } from 'react';

import { sortActiveAccounts } from '../../../accounts/components/AccountSearch/utils';
import { type Account, getAccountDisplayName, type SubscriptionState } from '../../../accounts/model';

import { type SearchResult } from './index';

type UseOmnibarOptions = {
  initialQuery?: string;
  accounts?: Account[];
  isAccountsLoading?: boolean;
  onSearchAccounts?: (query: string) => void;
  // Future: Add other data sources
  // projects?: Project[];
  // environments?: Environment[];
  // members?: Member[];
};

type UseOmnibarReturn = {
  searchResults: SearchResult[];
  isLoading: boolean;
  subscriptionFilter?: SubscriptionState;
  closeOmnibar: () => void;
  handleSearch: (query: string) => void;
  handleSubscriptionFilterChange: (filter?: SubscriptionState) => void;
};

export const useOmnibar = ({
  initialQuery = '',
  accounts = [],
  isAccountsLoading = false,
  onSearchAccounts,
}: UseOmnibarOptions = {}): UseOmnibarReturn => {
  const [currentQuery, setCurrentQuery] = useState(initialQuery);
  const [subscriptionFilter, setSubscriptionFilter] = useState<SubscriptionState | undefined>();

  // Convert accounts to search results
  const accountResults = useMemo((): SearchResult[] => {
    if (!currentQuery || currentQuery.length < 2) {
      return [];
    }

    // Sort accounts by subscription status (same as original implementation)
    const normalizedQuery = currentQuery.trim().toLowerCase();
    const exactId = /^[a-f0-9]{24}$/.test(normalizedQuery) ? normalizedQuery : null;
    const sortedAccounts = [...accounts].sort(
      (a, b) => Number(b._id === exactId) - Number(a._id === exactId) || sortActiveAccounts(a, b),
    );

    return sortedAccounts.map((account) => ({
      id: account._id,
      title: getAccountDisplayName(account),
      subtitle: account.subscription
        ? `${account.subscription.type} - ${account.subscription.state}`
        : 'No subscription',
      icon: 'organization',
      data: account,
    }));
  }, [accounts, currentQuery]);

  // Combine all search results
  const searchResults = useMemo((): SearchResult[] => {
    const results: SearchResult[] = [];

    // Add account results
    results.push(...accountResults);

    // TODO: Add other result types (projects, environments, members)
    // results.push(...projectResults);
    // results.push(...environmentResults);
    // results.push(...memberResults);

    return results;
  }, [accountResults]);

  // Handle closing the omnibar
  const closeOmnibar = useCallback(() => {
    setCurrentQuery('');
  }, []);

  // Handle search queries
  const handleSearch = useCallback(
    (query: string) => {
      setCurrentQuery(query);
      onSearchAccounts?.(query);
    },
    [onSearchAccounts],
  );

  // Handle subscription filter changes
  const handleSubscriptionFilterChange = useCallback((filter?: SubscriptionState) => {
    setSubscriptionFilter(filter);
  }, []);

  return {
    searchResults,
    isLoading: isAccountsLoading,
    subscriptionFilter,
    closeOmnibar,
    handleSearch,
    handleSubscriptionFilterChange,
  };
};

export default useOmnibar;
